import { FormikErrors } from 'formik'
import { useEffect, useState } from 'react'
import { deepCopy, removeElementAtIndex } from '../../helpers'
import { useModalControl } from '../../hooks/useModalControl'
import {
  AddDeliveryLocation,
  ProductAvailabilityPolicy,
} from '../../redux-toolkits/products/products.type'
import {
  useGetAllVendorsQuery,
  useGetSingleVendorQuery,
} from '../../redux-toolkits/vendors/vendors.slice'
import { CreateProductValues } from '../../redux-toolkits/products/products.type'
import { VendorDeliveryAreaRestriction } from '../common/vendorDeliveryAreaRestriction'
import { EditInput } from '../inputs/editInput'
import { AddDeliveryLocationModal } from '../modal/app_modals/addDeliveryLocation'
import { HeaderSubheader } from '../typography/HeaderSubheader'
import { ProductLocationChips } from './product-location-chips'
import { mapCreatorLocationsToForm } from './product-location-mappers'
import { ProductLocationModeSelector } from './product-location-mode-selector'

export const ManageProductLocations = ({
  values,
  setFieldValue,
  errors,
  typeTitle,
}: {
  values: CreateProductValues
  setFieldValue: (
    field: string,
    value: any,
    shouldValidate?: boolean,
  ) => Promise<void> | Promise<FormikErrors<CreateProductValues>>
  errors?: string
  typeTitle: string
}) => {
  const [selectedLocation, setSelectedLocation] = useState<{
    index: number
    item: AddDeliveryLocation
  }>()

  const { handleModalOpen, setModalOpen, modal, modalOpen, handleModalClose } =
    useModalControl()

  const { data: selectedCreatorData, isFetching: fetchingSelectedCreator } =
    useGetSingleVendorQuery(
      {
        id: values.vendor._id,
      },
      {
        skip: !values.vendor._id,
      },
    )

  const { data: creatorListData, isFetching: fetchingCreatorList } =
    useGetAllVendorsQuery(
      {
        page: '1',
        limit: '10',
        search: values.vendor.name || undefined,
      },
      {
        skip: !values.vendor._id || values.creatorDeliveryLocation.length > 0,
      },
    )

  const isInheritingCreatorLocations =
    values.availabilityPolicy === 'INHERIT_VENDOR'
  const selectedCreator =
    selectedCreatorData?.data?.profile ??
    creatorListData?.data?.vendors.find(
      (creator) => creator._id === values.vendor._id,
    )
  const defaultDeliveryAreaRestriction = {
    mode: 'ALL_REGION' as const,
    areaIds: [],
  }
  const inheritedDeliveryAreaRestriction =
    selectedCreator?.deliveryAreaRestriction ??
    values.effectiveDeliveryAreaRestriction ??
    defaultDeliveryAreaRestriction
  const displayedDeliveryAreaRestriction = isInheritingCreatorLocations
    ? inheritedDeliveryAreaRestriction
    : values.deliveryAreaRestriction
  const selectedCreatorLocations = selectedCreator
    ? mapCreatorLocationsToForm(selectedCreator)
    : values.creatorDeliveryLocation
  const customDeliveryLocations =
    values.deliveryLocation.length > 0
      ? values.deliveryLocation
      : selectedCreatorLocations
  const restrictionRegionSource =
    values.availabilityPolicy === 'CUSTOM'
      ? ({
          deliveryLocation: customDeliveryLocations.map((location) => ({
            deliveryTime: location.deliveryTime,
            locationId: {
              _id: location.locationId,
              name: location.locationName,
            },
            locationType: location.locationType,
          })),
        } as any)
      : selectedCreator
  const selectedCustomLocationIds = values.deliveryLocation.map(
    (location) => location.locationId,
  )

  const handleLocationModeChange = (mode: ProductAvailabilityPolicy) => {
    setFieldValue('availabilityPolicy', mode)
    setSelectedLocation(undefined)

    if (mode !== 'CUSTOM') {
      return
    }

    const nextLocations =
      values.deliveryLocation.length > 0
        ? values.deliveryLocation
        : selectedCreatorLocations

    if (values.deliveryLocation.length < 1 && nextLocations.length > 0) {
      setFieldValue('deliveryLocation', deepCopy(nextLocations))
      return
    }

    if (nextLocations.length < 1) {
      handleModalOpen('addDeliveryLocationModal')
    }
  }

  useEffect(() => {
    if (
      !selectedCreatorData?.data?.profile ||
      selectedCreatorData.data.profile._id !== values.vendor._id
    ) {
      return
    }

    const creatorLocations = mapCreatorLocationsToForm(
      selectedCreatorData.data.profile,
    )

    setFieldValue('creatorDeliveryLocation', creatorLocations, false)

    if (
      values.availabilityPolicy === 'CUSTOM' &&
      values.deliveryLocation.length < 1
    ) {
      setFieldValue('deliveryLocation', deepCopy(creatorLocations), false)
    }
  }, [
    selectedCreatorData,
    setFieldValue,
    values.availabilityPolicy,
    values.deliveryLocation.length,
    values.vendor._id,
  ])

  useEffect(() => {
    const creatorFromList = creatorListData?.data?.vendors.find(
      (creator) => creator._id === values.vendor._id,
    )

    if (!creatorFromList) {
      return
    }

    const creatorLocations = mapCreatorLocationsToForm(creatorFromList)

    setFieldValue('creatorDeliveryLocation', creatorLocations, false)

    if (
      values.availabilityPolicy === 'CUSTOM' &&
      values.deliveryLocation.length < 1
    ) {
      setFieldValue('deliveryLocation', deepCopy(creatorLocations), false)
    }
  }, [
    creatorListData,
    setFieldValue,
    values.availabilityPolicy,
    values.deliveryLocation.length,
    values.vendor._id,
  ])

  return (
    <div className="flex flex-col gap-6 border-divider-gray">
      <HeaderSubheader
        title="Location Availability"
        text={`Choose how this ${typeTitle} gets its delivery locations.`}
        className="w-full md:w-fit"
      />

      <ProductLocationModeSelector
        value={values.availabilityPolicy}
        onChange={handleLocationModeChange}
      />

      {isInheritingCreatorLocations ? (
        <div className="flex flex-col gap-3">
          <p className="text-base font-medium">Inherited creator locations:</p>
          <ProductLocationChips
            locations={values.creatorDeliveryLocation}
            emptyText={
              fetchingSelectedCreator || fetchingCreatorList
                ? 'Loading creator locations.'
                : values.vendor._id
                  ? 'This creator has no available delivery locations.'
                  : 'Select a creator to preview inherited locations.'
            }
          />
        </div>
      ) : (
        <EditInput
          title={`Custom Locations`}
          value={undefined}
          onEditClick={() => {
            handleModalOpen('addDeliveryLocationModal')
          }}
          errors={errors}
          onCancel={() => {}}
          onEditSave={() => {}}
          editing={false}
          className={'col-span-full'}
        >
          {() => {
            return (
              <ProductLocationChips
                locations={values.deliveryLocation}
                emptyText={`No custom locations added for this ${typeTitle.toLowerCase()}.`}
                editable
                onEdit={(idx, item) => {
                  setSelectedLocation({ index: idx, item })
                  handleModalOpen('addDeliveryLocationModal')
                }}
                onRemove={(idx) => {
                  const locationList = deepCopy(values.deliveryLocation ?? [])

                  setFieldValue(
                    'deliveryLocation',
                    removeElementAtIndex(locationList, idx),
                  )
                }}
              />
            )
          }}
        </EditInput>
      )}

      <VendorDeliveryAreaRestriction
        vendor={
          {
            deliveryAreaRestriction: displayedDeliveryAreaRestriction,
          } as any
        }
        deliveryRegionSource={restrictionRegionSource as any}
        loading={false}
        onModify={(data, closeCallback) => {
          if (isInheritingCreatorLocations) {
            closeCallback()
            return
          }

          setFieldValue('deliveryAreaRestriction', data.deliveryAreaRestriction)
          closeCallback()
        }}
        readOnly={isInheritingCreatorLocations}
      />

      <AddDeliveryLocationModal
        open={modalOpen && modal === 'addDeliveryLocationModal'}
        setOpen={setModalOpen}
        onCloseCallback={() => {
          handleModalClose()
          setSelectedLocation(undefined)
        }}
        onSuccessCallback={(item) => {
          const locations = deepCopy(values.deliveryLocation ?? [])
          const duplicateIndex = locations.findIndex(
            (location) => location.locationId === item.locationId,
          )

          if (
            duplicateIndex >= 0 &&
            duplicateIndex !== selectedLocation?.index
          ) {
            locations[duplicateIndex] = item
            if (selectedLocation) {
              locations.splice(selectedLocation.index, 1)
            }
          } else if (selectedLocation) {
            locations[selectedLocation.index] = item
          } else {
            locations.push(item)
          }
          setFieldValue('deliveryLocation', locations)
          setSelectedLocation(undefined)
          handleModalClose()
        }}
        location={selectedLocation?.item}
        mode={selectedLocation ? 'edit' : 'create'}
        disabledLocationIds={selectedCustomLocationIds}
      />
    </div>
  )
}
